// src/pages/Resources/UserAccessDetailsModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Spinner, Badge } from 'react-bootstrap';
import { FaUser, FaExternalLinkAlt } from 'react-icons/fa';

interface AccessItem {
  objectType: string;        // "Site", "List", "Library", "Folder", "File"
  objectName: string;
  objectUrl?: string;
  permissionLevel: string;
  grantedThrough: string;    // "Direct", "Group", "Sharing Link"
  groupName?: string;
  inheritedFrom?: string;
  hasUniquePermissions: boolean;
}

interface UserAccessDetails {
  userId: string;
  displayName: string;
  email: string;
  photoUrl?: string;
  accessItems: AccessItem[];
}

interface Props {
  show: boolean;
  userId: string;
  userName: string;
  internalResourceId: string;
  groupName: string;
  onClose: () => void;
}

const UserAccessDetailsModal: React.FC<Props> = ({
  show,
  userId,
  userName,
  internalResourceId,
  groupName,
  onClose
}) => {
  const [details, setDetails] = useState<UserAccessDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show && userId && internalResourceId) {
      loadUserAccessDetails();
    }
  }, [show, userId, internalResourceId]);

  const loadUserAccessDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/resources/${internalResourceId}/user/${userId}/access-details`,
        {
          headers: {
            Authorization: `Bearer ${await acquireToken()}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to load user access details');
      }

      const data: UserAccessDetails = await response.json();
      setDetails(data);
    } catch (err: any) {
      console.error('Error loading user access details:', err);
      setError(err.message || 'Failed to load access details');
    } finally {
      setLoading(false);
    }
  };

  const acquireToken = async () => {
    // Placeholder - should come from context/parent
    return 'token';
  };

  const getObjectIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'site': return '🌐';
      case 'list': return '📋';
      case 'library': return '📁';
      case 'folder': return '📂';
      case 'file': return '📄';
      default: return '📦';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUser className="me-2" />
          Access Details - {userName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="alert alert-info small mb-3">
          <strong>User Access Breakdown</strong>
          <p className="mb-0 mt-1">
            This shows all objects that <strong>{userName}</strong> has access to in{' '}
            <strong>{groupName}</strong> and how they received that access.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-muted">Loading access details...</div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            <strong>Error:</strong> {error}
          </div>
        ) : details ? (
          <>
            {/* User Info Card */}
            <div className="border rounded p-3 mb-4 bg-light">
              <div className="d-flex align-items-center gap-3">
                <div className="member-avatar">
                  {details.photoUrl ? (
                    <img
                      src={details.photoUrl}
                      alt={details.displayName}
                      style={{ 
                        width: '64px', 
                        height: '64px', 
                        borderRadius: '50%' 
                      }}
                    />
                  ) : (
                    <div 
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: '#0078d4',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        fontWeight: 'bold'
                      }}
                    >
                      {getInitials(details.displayName)}
                    </div>
                  )}
                </div>
                <div>
                  <h5 className="mb-1">{details.displayName}</h5>
                  <div className="text-muted">{details.email}</div>
                  <div className="mt-2">
                    <Badge bg="primary">{details.accessItems.length} objects accessible</Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Access Items Table */}
            {details.accessItems.length === 0 ? (
              <div className="text-center text-muted py-4">
                No specific object permissions found. User may have site-level access only.
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table hover size="sm">
                  <thead className="sticky-top bg-white">
                    <tr>
                      <th style={{ width: '5%' }}></th>
                      <th style={{ width: '30%' }}>Object</th>
                      <th style={{ width: '15%' }}>Type</th>
                      <th style={{ width: '15%' }}>Permission</th>
                      <th style={{ width: '25%' }}>Granted Through</th>
                      <th style={{ width: '10%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.accessItems.map((item, index) => (
                      <tr key={index}>
                        <td className="text-center">
                          {getObjectIcon(item.objectType)}
                        </td>
                        <td>
                          <div className="fw-semibold">{item.objectName}</div>
                          {item.inheritedFrom && (
                            <div className="small text-muted">
                              Inherited from: {item.inheritedFrom}
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge bg="secondary">{item.objectType}</Badge>
                          {item.hasUniquePermissions && (
                            <div className="small text-warning mt-1">
                              🔒 Unique permissions
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge bg="primary">{item.permissionLevel}</Badge>
                        </td>
                        <td>
                          <div>{item.grantedThrough}</div>
                          {item.groupName && (
                            <div className="small text-muted">
                              via {item.groupName}
                            </div>
                          )}
                        </td>
                        <td className="text-end">
                          {item.objectUrl && (
                            <Button
                              as="a"
                              href={item.objectUrl}
                              target="_blank"
                              rel="noreferrer"
                              variant="link"
                              size="sm"
                              title="Open in SharePoint"
                            >
                              <FaExternalLinkAlt />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserAccessDetailsModal;
